import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import { projectsApi } from '../../../api/projects';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { WorkspaceTabs } from '../../../components/WorkspaceTabs';
import { RecordHeader } from '../../../components/RecordHeader';
import { ProjectOverviewTab } from './ProjectOverviewTab';
import { ProjectLogframeTab } from './ProjectLogframeTab';
import { ProjectWorkplanTab } from './ProjectWorkplanTab';
import { ProjectBudgetTab } from './ProjectBudgetTab';
import { ProjectFinanceTab } from './ProjectFinanceTab';
import { ProjectBeneficiariesTab } from './ProjectBeneficiariesTab';
import { ProjectLocationsTab } from './ProjectLocationsTab';
import { ProjectImplementationTab } from './ProjectImplementationTab';
import { ProjectRiskTab } from './ProjectRiskTab';
import { ProjectMealTab } from './ProjectMealTab';
import { ProjectTeamTab } from './ProjectTeamTab';
import { ProjectApprovalsHistoryTab } from './ProjectApprovalsHistoryTab';
import { ProjectDocumentsTab } from './ProjectDocumentsTab';
import { ProjectAmendmentsTab } from './ProjectAmendmentsTab';
import { formatDate } from '../../../utils/formatDate';
import { isProjectDesignEditable } from './projectEditLock';

const STATUS_STYLES: Record<string, string> = {
  draft: 'status-badge-neutral', submitted: 'status-badge-warning', under_review: 'status-badge-info',
  approved: 'status-badge-success', in_progress: 'status-badge-info', completed: 'status-badge-success',
  rejected: 'status-badge-danger', closed: 'status-badge-neutral', cancelled: 'status-badge-danger',
  returned: 'status-badge-warning', clarification_required: 'status-badge-warning', archived: 'status-badge-neutral',
};

/**
 * Fetches the project ONCE at the workspace level and shares it with
 * every tab via a plain `project` prop - previously via route context
 * (useOutletContext) across 14 real nested routes. Converted to
 * WorkspaceTabs (client-side tab state, one URL) as the one consistent
 * workspace pattern used everywhere in VConnect now, per explicit
 * direction to unify every page onto a single implementation rather
 * than keep Projects as the one page with real nested routing.
 * Bookmarking/deep-linking a specific tab is the one real capability
 * this trades away versus the old nested-route version.
 */
export function ProjectWorkspaceLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const id = Number(projectId);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const canSubmit = usePermission('projects.submit');
  const canClose = usePermission('projects.close');

  const projectQuery = useQuery({ queryKey: ['project', id], queryFn: () => projectsApi.get(id) });
  const pendingApprovalsQuery = useQuery({ queryKey: ['pending-approvals'], queryFn: () => projectsApi.pendingApprovals() });
  const myPendingApproval = pendingApprovalsQuery.data?.data.find((a) => a.record_id === id);

  const invalidateProject = () => queryClient.invalidateQueries({ queryKey: ['project', id] });

  const submitMutation = useMutation({
    mutationFn: () => projectsApi.submit(id),
    onSuccess: (result) => { showSuccess(`Submitted - ${result.data.approversAssigned} approver(s) notified.`); invalidateProject(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit project.'),
  });

  const closeMutation = useMutation({
    mutationFn: () => projectsApi.close(id),
    onSuccess: () => { showSuccess('Project closed.'); invalidateProject(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to close project.'),
  });

  const [decisionComment, setDecisionComment] = useState('');
  const decideMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => {
      if (!myPendingApproval) throw new Error('No pending approval found.');
      return projectsApi.decide(id, { approvalId: myPendingApproval.id, decision, comment: decisionComment || undefined });
    },
    onSuccess: (_r, decision) => {
      showSuccess(decision === 'approved' ? 'Project approved.' : 'Project rejected.');
      invalidateProject();
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      setDecisionComment('');
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  if (!projectQuery.data) {
    return <div className="page"><p className="muted">Loading project…</p></div>;
  }

  const project = projectQuery.data.data;
  const durationDays = Math.round((new Date(project.end_date).getTime() - new Date(project.start_date).getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div className="page project-workspace">
      <RecordHeader
        title={project.project_name}
        entityType="project"
        entityId={project.id}
        fields={[
          { label: 'Reference', value: project.project_reference },
          { label: 'Start date', value: formatDate(project.start_date) },
          { label: 'End date', value: formatDate(project.end_date) },
          { label: 'Duration', value: `${durationDays} days` },
          { label: 'Total budget', value: `${project.currency_code} ${Number(project.budget_total).toLocaleString()}` },
          { label: 'Project type', value: project.project_type },
          { label: 'Status', value: <span className={`status-badge ${STATUS_STYLES[project.status] ?? 'status-badge-neutral'}`}>{project.status.replace('_', ' ').toUpperCase()}</span> },
        ]}
        actions={
          <>
            {canSubmit && project.status === 'draft' && (
              <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>
                {submitMutation.isPending ? 'Submitting…' : 'Submit for Review'}
              </button>
            )}
            {canClose && project.status === 'completed' && (
              <button onClick={() => closeMutation.mutate()} disabled={closeMutation.isPending}>
                {closeMutation.isPending ? 'Closing…' : 'Close Project'}
              </button>
            )}
          </>
        }
      />

      {myPendingApproval && (
        <div className="approval-panel">
          <h3>Your approval is pending on this project</h3>
          <p className="muted">Sequence {myPendingApproval.sequence} - decision required before this project can move forward.</p>
          <label>Comment (optional)<input value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} /></label>
          <div className="form-actions">
            <button onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}>
              <CheckCircle2 size={16} className="inline-icon" /> Approve
            </button>
            <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}>
              <XCircle size={16} className="inline-icon" /> Reject
            </button>
          </div>
        </div>
      )}

      {!isProjectDesignEditable(project.status) && (
        <div className="warning-state project-lock-banner">
          <strong>Design locked.</strong>&nbsp;This project is {project.status.replace(/_/g, ' ')}, so its design and budget can't be edited
          {['approved', 'active', 'completed', 'closed'].includes(project.status) ? ' - changes go through an amendment.' : ' unless it is returned for redraft.'}
          &nbsp;Indicator actuals, risks, team, comments and documents remain editable.
        </div>
      )}

      <WorkspaceTabs
        tabs={[
          { key: 'overview', label: 'Overview', content: <ProjectOverviewTab project={project} /> },
          { key: 'logical-framework', label: 'Logical Framework', content: <ProjectLogframeTab project={project} /> },
          { key: 'workplan', label: 'Work Plan', content: <ProjectWorkplanTab project={project} /> },
          { key: 'budget', label: 'Budget', content: <ProjectBudgetTab project={project} /> },
          { key: 'finance', label: 'Finance', content: <ProjectFinanceTab project={project} /> },
          { key: 'beneficiaries', label: 'Beneficiaries', content: <ProjectBeneficiariesTab project={project} /> },
          { key: 'locations', label: 'Locations', content: <ProjectLocationsTab project={project} /> },
          { key: 'implementation', label: 'Implementation', content: <ProjectImplementationTab project={project} /> },
          { key: 'risk', label: 'Risk & Safeguarding', content: <ProjectRiskTab project={project} /> },
          { key: 'meal', label: 'Monitoring & MEAL', content: <ProjectMealTab project={project} /> },
          { key: 'team', label: 'Team', content: <ProjectTeamTab project={project} /> },
          { key: 'amendments', label: 'Amendments', content: <ProjectAmendmentsTab project={project} /> },
          { key: 'documents', label: 'Documents', content: <ProjectDocumentsTab project={project} /> },
          { key: 'tracking', label: 'Tracking', content: <ProjectApprovalsHistoryTab project={project} /> },
        ]}
      />
    </div>
  );
}
